import { useEffect, useState, type ReactNode } from "react";
import { Image, Pressable, ScrollView, Text, View } from "react-native";
import {
  AlertCircle,
  Check,
  ChevronDown,
  ChevronRight,
  Plus,
  RefreshCw,
} from "lucide-react-native";
import {
  Dialog,
  DialogHeader,
  PrimaryButton,
  SecondaryButton,
  DocumentRow,
  BackLink,
} from "@louise/moon-ui";
import { semanticColors } from "@src/packages/moon-ui/theme/tokens";
import { ThinkingOrbStatus } from "@src/components/ui/ThinkingOrbStatus";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
const documentUploadIllustration =
  require("@src/apps/moon/assets/images/document-upload-illustration.png") as number;
const documentReviewIllustration =
  require("@src/apps/moon/assets/images/document-review-icons/review-found-3d.png") as number;
const uploadFileIcons = {
  document:
    require("@src/apps/moon/assets/images/upload-file-icons/document-3d.png") as number,
  image:
    require("@src/apps/moon/assets/images/upload-file-icons/image-3d.png") as number,
  generic:
    require("@src/apps/moon/assets/images/upload-file-icons/generic-3d.png") as number,
} as const;
const reviewCategoryIcons = {
  treatmentPlan:
    require("@src/apps/moon/assets/images/document-review-icons/treatment-plan-3d.png") as number,
  monitoringAppointment:
    require("@src/apps/moon/assets/images/document-review-icons/monitoring-appointment-3d.png") as number,
  sourceDocuments:
    require("@src/apps/moon/assets/images/document-review-icons/source-documents-3d.png") as number,
} as const;

const REVIEW_GROUPS = [
  {
    id: "treatmentPlan",
    image: reviewCategoryIcons.treatmentPlan,
    accessibilityLabel: "Treatment plan illustration",
    title: "Treatment plan",
    detail: "3 medication changes",
    fields: [
      {
        label: "Changes",
        value: "3 medication changes",
        detail: "High confidence",
      },
      { label: "Source", value: "treatment-plan.pdf", detail: "Pages 1–2" },
      { label: "Destination", value: "Treatment plan", detail: "Care record" },
    ],
    helper:
      "Tap Changes to see each medication update. Save them now, or review each one before adding them to your records.",
  },
  {
    id: "monitoringAppointment",
    image: reviewCategoryIcons.monitoringAppointment,
    accessibilityLabel: "Monitoring appointment illustration",
    title: "Monitoring appointment",
    detail: "May 20 · Clinic visit",
    fields: [
      { label: "Appointment", value: "May 20, 2026", detail: "Clinic visit" },
      {
        label: "Source",
        value: "appointment-letter.pdf",
        detail: "Page 1",
      },
      { label: "Destination", value: "Monitoring", detail: "Care plan" },
    ],
    helper:
      "Save the appointment as shown, or review the date and clinic details first.",
  },
  {
    id: "sourceDocuments",
    image: reviewCategoryIcons.sourceDocuments,
    accessibilityLabel: "Source documents illustration",
    title: "Source documents",
    detail: "2 files will stay attached",
    fields: [
      {
        label: "Attachments",
        value: "2 source documents",
        detail: "Ready to save",
      },
      {
        label: "Destination",
        value: "Source documents",
        detail: "Care record",
      },
      { label: "Originals", value: "Keep attached", detail: "No changes" },
    ],
    helper:
      "These originals can be saved without a structured-data review. You can still inspect them first.",
  },
] as const;

type ReviewGroup = (typeof REVIEW_GROUPS)[number]["id"];

export const DOCUMENT_UPLOAD_PROTOTYPE_STATES = [
  "single-upload",
  "single-processing",
  "multi-upload",
  "background-handoff",
  "single-review",
  "source-only",
  "ambiguous-review",
  "multi-review",
  "partial-failure",
] as const;

export type DocumentUploadPrototypeState =
  (typeof DOCUMENT_UPLOAD_PROTOTYPE_STATES)[number];

type UploadItemStatus =
  | "queued"
  | "uploading"
  | "processing"
  | "complete"
  | "saved"
  | "needs_review"
  | "failed";

type UploadItem = {
  id: string;
  name: string;
  detail: string;
  progress?: number;
  status: UploadItemStatus;
};

type Props = {
  state: DocumentUploadPrototypeState;
  visible: boolean;
  onClose: () => void;
};

const COLORS = {
  text: semanticColors.text.primary,
  secondary: semanticColors.text.secondary,
  faint: semanticColors.text.tertiary,
  border: semanticColors.border.subtle,
  surfaceStrong: semanticColors.surface.input,
  ember: semanticColors.action.primary.background,
  warm: semanticColors.status.warning,
  warning: semanticColors.status.warning,
  error: semanticColors.status.danger,
  blue: semanticColors.status.info,
} as const;

const STATE_ITEMS: Record<DocumentUploadPrototypeState, UploadItem[]> = {
  "single-upload": [
    {
      id: "amh",
      name: "amh-lab-report.pdf",
      detail: "4.2 MB · Secure upload",
      progress: 68,
      status: "uploading",
    },
  ],
  "single-processing": [
    {
      id: "amh",
      name: "amh-lab-report.pdf",
      detail: "Upload complete · Reading now",
      status: "processing",
    },
  ],
  "multi-upload": [
    {
      id: "amh",
      name: "amh-lab-report.pdf",
      detail: "4.2 MB",
      progress: 68,
      status: "uploading",
    },
    {
      id: "afc",
      name: "afc-ultrasound.pdf",
      detail: "8.1 MB",
      status: "processing",
    },
    {
      id: "insurance",
      name: "insurance-card-front.jpg",
      detail: "1.8 MB",
      progress: 100,
      status: "complete",
    },
    {
      id: "consent",
      name: "consent-forms.pdf",
      detail: "2.6 MB",
      status: "queued",
    },
  ],
  "background-handoff": [
    {
      id: "packet",
      name: "clinic-records-packet.pdf",
      detail: "42 pages",
      status: "processing",
    },
    {
      id: "results",
      name: "outside-results.pdf",
      detail: "18 pages",
      status: "processing",
    },
    {
      id: "card",
      name: "insurance-card-back.jpg",
      detail: "1.9 MB",
      status: "needs_review",
    },
  ],
  "single-review": [
    {
      id: "amh",
      name: "amh-lab-report.pdf",
      detail: "Processed just now · 1 result found",
      status: "complete",
    },
  ],
  "source-only": [
    {
      id: "scanned-notes",
      name: "scanned-clinic-notes.pdf",
      detail: "Original file saved · No structured data found",
      status: "saved",
    },
  ],
  "ambiguous-review": [
    {
      id: "handwritten-result",
      name: "handwritten-result.jpg",
      detail: "Processed · Confirmation needed",
      status: "needs_review",
    },
  ],
  "multi-review": [
    {
      id: "plan",
      name: "treatment-plan.pdf",
      detail: "3 proposed updates",
      status: "needs_review",
    },
    {
      id: "letter",
      name: "appointment-letter.pdf",
      detail: "1 proposed update",
      status: "needs_review",
    },
  ],
  "partial-failure": [
    {
      id: "amh",
      name: "amh-lab-report.pdf",
      detail: "Saved to your records",
      status: "saved",
    },
    {
      id: "afc",
      name: "afc-ultrasound.pdf",
      detail: "Could not read this file",
      status: "failed",
    },
    {
      id: "insurance",
      name: "insurance-card-front.jpg",
      detail: "Saved · Review details when ready",
      status: "needs_review",
    },
  ],
};

const STATE_COPY: Record<
  DocumentUploadPrototypeState,
  { title: string; subtitle: string }
> = {
  "single-upload": {
    title: "Uploading your document",
    subtitle: "We will read it automatically after the secure upload finishes.",
  },
  "single-processing": {
    title: "Reading your document",
    subtitle:
      "The upload is complete. Louise is looking for information you can review.",
  },
  "multi-upload": {
    title: "Uploading your documents",
    subtitle: "Each file shows its own current step.",
  },
  "background-handoff": {
    title: "Louise is reading your files",
    subtitle:
      "This may take a few minutes. You can keep using Louise while we work. We’ll let you know when your files are ready to review.",
  },
  "single-review": {
    title: "Review the information we found",
    subtitle: "We found one result. Check it before saving it to your records.",
  },
  "source-only": {
    title: "Your document is saved",
    subtitle:
      "We kept the original file, but did not find structured information to add.",
  },
  "ambiguous-review": {
    title: "Review needed before saving",
    subtitle:
      "We found a possible result, but a few details need your confirmation.",
  },
  "multi-review": {
    title: "Review what we found",
    subtitle:
      "These files contain information for more than one part of your care.",
  },
  "partial-failure": {
    title: "Some files need attention",
    subtitle:
      "Two files are safe. One file could not be read and can be retried.",
  },
};

function FileTypeIcon({ fileName }: { fileName: string }) {
  const extension = fileName.split(".").pop()?.toLowerCase();
  const kind = extension
    ? ["jpg", "jpeg", "png", "gif", "webp", "heic"].includes(extension)
      ? "image"
      : ["pdf", "doc", "docx", "txt", "csv", "xls", "xlsx"].includes(extension)
        ? "document"
        : "generic"
    : "generic";

  return (
    <Image
      accessibilityLabel={`${extension ?? "generic"} file`}
      source={uploadFileIcons[kind]}
      resizeMode="contain"
      style={{ height: 34, width: 34 }}
    />
  );
}

function ItemStatus({ status }: { status: UploadItemStatus }) {
  if (status === "complete" || status === "saved") {
    return (
      <View
        accessibilityLabel={status === "saved" ? "Saved" : "Processed"}
        style={{
          alignItems: "center",
          backgroundColor: "rgba(255, 255, 255, 0.66)",
          borderColor: "rgba(42, 37, 34, 0.09)",
          borderRadius: 999,
          borderWidth: 1,
          flexDirection: "row",
          gap: 5,
          minHeight: 30,
          justifyContent: "center",
          paddingHorizontal: 10,
          paddingVertical: 4,
        }}
      >
        <Check color="rgba(42, 37, 34, 0.52)" size={13} strokeWidth={2.4} />
        <Text
          className="font-dm-sans"
          style={{
            color: "rgba(42, 37, 34, 0.62)",
            fontSize: 11,
            fontWeight: "700",
          }}
        >
          {status === "saved" ? "Saved" : "Processed"}
        </Text>
      </View>
    );
  }
  if (status === "failed") {
    return <AlertCircle size={16} color={COLORS.error} strokeWidth={2.2} />;
  }
  return null;
}

function orbStateForStatus(status: UploadItemStatus) {
  switch (status) {
    case "uploading":
      // Keep row-level transfer visually distinct from the header's thinking orb.
      return "composing" as const;
    case "processing":
      // The morphing shape communicates active reading and transformation.
      return "shaping" as const;
    case "needs_review":
      return "solving" as const;
    case "queued":
      return "listening" as const;
    default:
      return null;
  }
}

function orbLabelForItem(item: UploadItem) {
  switch (item.status) {
    case "uploading":
      return item.progress === undefined
        ? "Uploading…"
        : `Uploading ${item.progress}%`;
    case "processing":
      return "Reading…";
    case "needs_review":
      return "Review ready";
    case "queued":
      return "Next up";
    default:
      return "";
  }
}

function UploadItemRow({
  item,
  onCancel,
}: {
  item: UploadItem;
  onCancel?: () => void;
}) {
  const canCancel =
    item.status === "queued" ||
    item.status === "uploading" ||
    item.status === "processing";
  const subtitle = item.detail;
  const orbState = orbStateForStatus(item.status);

  return (
    <DocumentRow
      title={item.name}
      subtitle={subtitle}
      leading={<FileTypeIcon fileName={item.name} />}
      trailing={
        orbState ? (
          <ThinkingOrbStatus
            accessibilityLabel={`${orbLabelForItem(item)} ${item.name}`}
            label={orbLabelForItem(item)}
            onCancel={canCancel ? onCancel : undefined}
            state={orbState}
            size={20}
          />
        ) : (
          <ItemStatus status={item.status} />
        )
      }
      showChevron={false}
      titleNumberOfLines={1}
      subtitleNumberOfLines={1}
      style={{ paddingVertical: 9 }}
    />
  );
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <Text
      className="font-dm-sans"
      style={{
        color: COLORS.faint,
        fontSize: 10,
        fontWeight: "700",
        letterSpacing: 0.8,
        textTransform: "uppercase",
      }}
    >
      {children}
    </Text>
  );
}

function ReviewField({
  label,
  value,
  detail,
  onPress,
  expanded = false,
  expandedContent,
}: {
  label: string;
  value: string;
  detail: string;
  onPress?: () => void;
  expanded?: boolean;
  expandedContent?: ReactNode;
}) {
  const capturePress = usePressCapture("DocumentReviewField");
  const row = (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        paddingVertical: 10,
      }}
    >
      <View style={{ flex: 1 }}>
        <Text
          className="font-dm-sans"
          style={{ color: COLORS.faint, fontSize: 10, fontWeight: "700" }}
        >
          {label}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            color: COLORS.text,
            fontSize: 14,
            fontWeight: "700",
            marginTop: 3,
          }}
        >
          {value}
        </Text>
      </View>
      <Text
        className="font-dm-sans"
        style={{ color: COLORS.secondary, fontSize: 11 }}
      >
        {detail}
      </Text>
      {onPress ? (
        expanded ? (
          <ChevronDown size={15} color={COLORS.faint} />
        ) : (
          <ChevronRight size={15} color={COLORS.faint} />
        )
      ) : null}
    </View>
  );

  return (
    <View
      style={{
        borderBottomWidth: 1,
        borderBottomColor: COLORS.border,
      }}
    >
      {onPress ? (
        <Pressable
          accessibilityLabel={`${label}: ${value}`}
          accessibilityRole="button"
          accessibilityState={{ expanded }}
          onPress={() => {
            capturePress(pressLabel(label), "review_field_row");
            onPress();
          }}
        >
          {row}
        </Pressable>
      ) : (
        row
      )}
      {expanded && expandedContent ? (
        <View style={{ gap: 8, paddingBottom: 12 }}>{expandedContent}</View>
      ) : null}
    </View>
  );
}

type ReviewFieldDetail = { title: string; detail: string };

function reviewFieldDetails(
  group: ReviewGroup,
  fieldLabel: string,
): ReviewFieldDetail[] | null {
  if (group === "treatmentPlan" && fieldLabel === "Changes") {
    return [
      { title: "Letrozole", detail: "Dose change · 5 mg daily" },
      { title: "Progesterone", detail: "Schedule change · nightly" },
      { title: "Prenatal vitamin", detail: "Added to plan · daily" },
    ];
  }

  if (group === "monitoringAppointment" && fieldLabel === "Appointment") {
    return [
      { title: "Date", detail: "May 20, 2026" },
      { title: "Visit type", detail: "Clinic visit" },
      { title: "Source", detail: "appointment-letter.pdf · Page 1" },
    ];
  }

  if (group === "sourceDocuments" && fieldLabel === "Attachments") {
    return [
      { title: "treatment-plan.pdf", detail: "Original attached" },
      { title: "appointment-letter.pdf", detail: "Original attached" },
    ];
  }

  return null;
}

function ReviewGroupDetail({
  group,
  isReviewing,
  onBack,
}: {
  group: ReviewGroup;
  isReviewing: boolean;
  onBack: () => void;
}) {
  const reviewGroup = REVIEW_GROUPS.find((candidate) => candidate.id === group);
  const [expandedField, setExpandedField] = useState<string | null>(null);
  if (!reviewGroup) return null;

  return (
    <View style={{ gap: 10 }}>
      <BackLink
        label="All proposed updates"
        accessibilityLabel="Back to proposed updates"
        action="back_to_proposed_updates"
        onPress={onBack}
        style={{ paddingVertical: 2 }}
      />

      <View style={{ alignItems: "center", flexDirection: "row", gap: 10 }}>
        <Image
          accessibilityLabel={reviewGroup.accessibilityLabel}
          source={reviewGroup.image}
          resizeMode="contain"
          style={{ width: 42, height: 42 }}
        />
        <View style={{ flex: 1 }}>
          <Text
            className="font-dm-sans"
            style={{ color: COLORS.text, fontSize: 13, fontWeight: "700" }}
          >
            {reviewGroup.title}
          </Text>
          <Text
            className="font-dm-sans"
            style={{ color: COLORS.secondary, fontSize: 11, marginTop: 3 }}
          >
            {reviewGroup.detail}
          </Text>
        </View>
      </View>

      <SectionLabel>
        {isReviewing ? "Review before saving" : "What Louise found"}
      </SectionLabel>
      <View>
        {reviewGroup.fields.map((field) => {
          const details = reviewFieldDetails(group, field.label);
          const isExpanded = expandedField === field.label;

          return (
            <ReviewField
              key={field.label}
              {...field}
              onPress={
                details
                  ? () => setExpandedField(isExpanded ? null : field.label)
                  : undefined
              }
              expanded={isExpanded}
              expandedContent={
                details
                  ? details.map((item) => (
                      <View
                        key={item.title}
                        style={{
                          flexDirection: "row",
                          alignItems: "flex-start",
                          gap: 8,
                          paddingLeft: 2,
                        }}
                      >
                        <View
                          style={{
                            width: 5,
                            height: 5,
                            marginTop: 5,
                            borderRadius: 3,
                            backgroundColor: COLORS.warm,
                          }}
                        />
                        <View style={{ flex: 1 }}>
                          <Text
                            className="font-dm-sans"
                            style={{
                              color: COLORS.text,
                              fontSize: 12,
                              fontWeight: "700",
                            }}
                          >
                            {item.title}
                          </Text>
                          <Text
                            className="font-dm-sans"
                            style={{
                              color: COLORS.secondary,
                              fontSize: 11,
                              marginTop: 1,
                            }}
                          >
                            {item.detail}
                          </Text>
                        </View>
                      </View>
                    ))
                  : null
              }
            />
          );
        })}
      </View>
      <Text
        className="font-dm-sans"
        style={{ color: COLORS.faint, fontSize: 11, lineHeight: 16 }}
      >
        {isReviewing
          ? "Review each field before saving. The original document will stay attached to this update."
          : reviewGroup.helper}
      </Text>
    </View>
  );
}

function ReviewContent({
  state,
  selectedGroup,
  isReviewing,
  isReviewingSingle,
  onSelectGroup,
  onBack,
}: {
  state: DocumentUploadPrototypeState;
  selectedGroup: ReviewGroup | null;
  isReviewing: boolean;
  isReviewingSingle: boolean;
  onSelectGroup: (group: ReviewGroup) => void;
  onBack: () => void;
}) {
  const capturePress = usePressCapture("DocumentReviewContent");
  if (state === "single-review" || state === "ambiguous-review") {
    return (
      <View style={{ gap: 10 }}>
        <SectionLabel>
          {state === "ambiguous-review" || isReviewingSingle
            ? "Review before saving"
            : "Extracted information"}
        </SectionLabel>
        {state === "ambiguous-review" ? (
          <View
            style={{
              alignItems: "flex-start",
              flexDirection: "row",
              gap: 8,
              padding: 12,
              borderRadius: 12,
              backgroundColor: "rgba(190, 137, 68, 0.1)",
              borderWidth: 1,
              borderColor: "rgba(190, 137, 68, 0.22)",
            }}
          >
            <AlertCircle color={COLORS.warm} size={16} strokeWidth={2} />
            <Text
              className="font-dm-sans"
              style={{
                flex: 1,
                color: COLORS.secondary,
                fontSize: 11,
                lineHeight: 16,
              }}
            >
              Louise found a possible result, but the source is difficult to
              read. Confirm the values before they are saved.
            </Text>
          </View>
        ) : null}
        <View>
          <ReviewField label="Test" value="AMH" detail="Page 1" />
          <ReviewField
            label="Result"
            value="2.8 ng/mL"
            detail={
              state === "ambiguous-review"
                ? "Needs confirmation"
                : "High confidence"
            }
          />
          <ReviewField label="Date" value="May 12, 2026" detail="Page 1" />
        </View>
        <Text
          className="font-dm-sans"
          style={{ color: COLORS.faint, fontSize: 11, lineHeight: 16 }}
        >
          {state === "ambiguous-review"
            ? "Check the result against the original image. The original document stays attached so you can refer back to it."
            : "You can edit any field before saving. The original document stays attached to this result."}
        </Text>
      </View>
    );
  }

  if (state === "source-only") {
    return (
      <View style={{ gap: 10 }}>
        <SectionLabel>Original file</SectionLabel>
        <View
          style={{ alignItems: "flex-start", flexDirection: "row", gap: 10 }}
        >
          <FileTypeIcon fileName="scanned-clinic-notes.pdf" />
          <View style={{ flex: 1 }}>
            <Text
              className="font-dm-sans"
              style={{ color: COLORS.text, fontSize: 13, fontWeight: "700" }}
            >
              Nothing to review yet
            </Text>
            <Text
              className="font-dm-sans"
              style={{
                color: COLORS.secondary,
                fontSize: 11,
                lineHeight: 16,
                marginTop: 3,
              }}
            >
              The original document is safely saved. You can open it from your
              documents later if you need to refer to it.
            </Text>
          </View>
        </View>
      </View>
    );
  }

  if (state === "multi-review") {
    if (selectedGroup) {
      return (
        <ReviewGroupDetail
          group={selectedGroup}
          isReviewing={isReviewing}
          onBack={onBack}
        />
      );
    }

    return (
      <View style={{ gap: 10 }}>
        <SectionLabel>Proposed updates</SectionLabel>
        {REVIEW_GROUPS.map(
          ({ id, image, accessibilityLabel, title, detail }) => (
            <Pressable
              key={id}
              accessibilityLabel={`Open ${title} proposed updates`}
              accessibilityRole="button"
              onPress={() => {
                capturePress(pressLabel(title), "open_review_group");
                onSelectGroup(id);
              }}
              style={({ pressed }) => [
                {
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 10,
                  paddingVertical: 4,
                },
                pressed ? { opacity: 0.78 } : null,
              ]}
            >
              <Image
                accessibilityLabel={accessibilityLabel}
                source={image}
                resizeMode="contain"
                style={{ width: 42, height: 42 }}
              />
              <View style={{ flex: 1 }}>
                <Text
                  className="font-dm-sans"
                  style={{
                    color: COLORS.text,
                    fontSize: 13,
                    fontWeight: "700",
                  }}
                >
                  {title}
                </Text>
                <Text
                  className="font-dm-sans"
                  style={{
                    color: COLORS.secondary,
                    fontSize: 11,
                    marginTop: 3,
                  }}
                >
                  {detail}
                </Text>
              </View>
              <ChevronRight size={15} color={COLORS.faint} />
            </Pressable>
          ),
        )}
        <Text
          className="font-dm-sans"
          style={{ color: COLORS.faint, fontSize: 11, lineHeight: 16 }}
        >
          Choose an update to see what will be added. You can save directly or
          review the details first.
        </Text>
      </View>
    );
  }

  return null;
}

export default function DocumentUploadInteractionPrototype({
  state,
  visible,
  onClose,
}: Props) {
  const [singleTimelineStage, setSingleTimelineStage] = useState<
    "uploading" | "processing" | "review"
  >(state === "single-processing" ? "processing" : "uploading");
  const [timelineProgress, setTimelineProgress] = useState(68);
  const [cancelledItemIds, setCancelledItemIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [additionalItems, setAdditionalItems] = useState<UploadItem[]>([]);
  const [selectedReviewGroup, setSelectedReviewGroup] =
    useState<ReviewGroup | null>(null);
  const [isReviewingGroup, setIsReviewingGroup] = useState(false);
  const [isReviewingSingle, setIsReviewingSingle] = useState(false);
  const [outcome, setOutcome] = useState<"saved" | "continued" | null>(null);
  const [retryingFailed, setRetryingFailed] = useState(false);
  const [retriedFailed, setRetriedFailed] = useState(false);

  useEffect(() => {
    setSingleTimelineStage(
      state === "single-processing" ? "processing" : "uploading",
    );
    setTimelineProgress(68);
    setCancelledItemIds(new Set());
    setAdditionalItems([]);
    setSelectedReviewGroup(null);
    setIsReviewingGroup(false);
    setIsReviewingSingle(false);
    setOutcome(null);
    setRetryingFailed(false);
    setRetriedFailed(false);
  }, [state]);

  useEffect(() => {
    if (state !== "single-upload") return;

    const progressTimer = setInterval(() => {
      setTimelineProgress((current) => Math.min(100, current + 8));
    }, 420);
    const processingTimer = setTimeout(() => {
      setSingleTimelineStage("processing");
    }, 2800);
    const reviewTimer = setTimeout(() => {
      setSingleTimelineStage("review");
    }, 6200);

    return () => {
      clearInterval(progressTimer);
      clearTimeout(processingTimer);
      clearTimeout(reviewTimer);
    };
  }, [state]);

  useEffect(() => {
    if (!retryingFailed) return;

    const retryTimer = setTimeout(() => {
      setRetryingFailed(false);
      setRetriedFailed(true);
    }, 2200);

    return () => clearTimeout(retryTimer);
  }, [retryingFailed]);

  const displayedState: DocumentUploadPrototypeState =
    state !== "single-upload"
      ? state
      : singleTimelineStage === "processing"
        ? "single-processing"
        : singleTimelineStage === "review"
          ? "single-review"
          : "single-upload";
  const copyKey =
    state === "single-upload" &&
    additionalItems.length > 0 &&
    displayedState === "single-upload"
      ? "multi-upload"
      : displayedState;
  const copy = STATE_COPY[copyKey];
  const baseItems = STATE_ITEMS[displayedState].map((item) =>
    state === "single-upload" && displayedState === "single-upload"
      ? { ...item, progress: timelineProgress }
      : item,
  );
  const itemsWithAddedFiles = [...baseItems, ...additionalItems];
  const items = itemsWithAddedFiles
    .map((item) => {
      if (state === "partial-failure" && item.id === "afc") {
        if (retriedFailed) {
          return {
            ...item,
            detail: "Saved to your records",
            status: "saved" as const,
          };
        }
        if (retryingFailed) {
          return {
            ...item,
            detail: "Reading again…",
            status: "processing" as const,
          };
        }
      }
      if (
        outcome === "saved" &&
        item.status !== "failed" &&
        item.status !== "queued" &&
        item.status !== "processing"
      ) {
        return {
          ...item,
          detail: "Saved to your records",
          status: "saved" as const,
        };
      }
      return item;
    })
    .filter((item) => !cancelledItemIds.has(item.id));
  const isReview =
    displayedState === "single-review" ||
    displayedState === "ambiguous-review" ||
    displayedState === "multi-review";
  const isBackground = displayedState === "background-handoff";
  const isPartial = displayedState === "partial-failure";
  const isSourceOnly = displayedState === "source-only";
  const isActive =
    displayedState === "single-upload" ||
    displayedState === "single-processing" ||
    displayedState === "multi-upload" ||
    displayedState === "background-handoff";
  const hasAddedFiles = additionalItems.length > 0;
  const isQueue = displayedState === "multi-upload" || hasAddedFiles;
  const headerOrbState =
    isBackground || displayedState === "single-processing"
      ? "shaping"
      : "working";
  const headerOrbLabel = isBackground
    ? "Reading your files"
    : displayedState === "single-processing"
      ? "Reading your document"
      : "Uploading files";
  const outcomeCopy =
    outcome === "saved"
      ? {
          title: "Saved to your records",
          subtitle: "The information is now available in your care records.",
        }
      : outcome === "continued"
        ? {
            title: "You can keep going",
            subtitle:
              "The files Louise could read are ready. You can retry the remaining file whenever you are ready.",
          }
        : null;
  const headerTitle =
    outcomeCopy?.title ?? (isActive ? `${copy.title}…` : copy.title);
  const headerSubtitle = outcomeCopy?.subtitle ?? copy.subtitle;
  const showOutcome = outcome !== null;

  const addMoreFiles = () => {
    if (additionalItems.length >= 3) return;
    const samples = [
      {
        id: "added-notes",
        name: "follow-up-notes.pdf",
        detail: "Queued · Added just now",
        status: "queued" as const,
      },
      {
        id: "added-image",
        name: "medication-label.jpg",
        detail: "Queued · Added just now",
        status: "queued" as const,
      },
      {
        id: "added-unknown",
        name: "supporting-file.bin",
        detail: "Queued · Added just now",
        status: "queued" as const,
      },
    ];
    setAdditionalItems((current) => {
      const next = samples[current.length];
      return next ? [...current, next] : current;
    });
  };

  return (
    <Dialog
      size="wide"
      visible={visible}
      onClose={onClose}
      animationType="fade"
      closeButton="minimal"
      surfaceStyle={{ maxHeight: "100%" }}
      contentStyle={{
        paddingHorizontal: 24,
        paddingTop: 24,
        paddingBottom: 22,
      }}
      testID={`document-upload-prototype-${state}`}
    >
      <ScrollView showsVerticalScrollIndicator={false}>
        <View style={{ gap: 18 }}>
          <View style={{ alignItems: "center", gap: 2 }}>
            {isActive ? (
              <ThinkingOrbStatus
                accessibilityLabel={headerOrbLabel}
                state={headerOrbState}
                variant="hero"
                size={64}
              />
            ) : (
              <Image
                source={
                  isReview
                    ? documentReviewIllustration
                    : documentUploadIllustration
                }
                resizeMode="contain"
                accessibilityLabel={
                  isReview
                    ? "Illustration of documents ready for review"
                    : "Illustration of documents being uploaded"
                }
                style={{ width: 84, height: 84 }}
              />
            )}
            <DialogHeader
              align="center"
              title={headerTitle}
              subtitle={headerSubtitle}
              style={{ alignItems: "center", width: "100%" }}
              subtitleStyle={{ marginTop: 0, textAlign: "center" }}
            />
          </View>

          <View style={{ gap: 9 }}>
            {isReview || isSourceOnly ? (
              <SectionLabel>Files</SectionLabel>
            ) : null}
            {items.map((item) => (
              <UploadItemRow
                key={item.id}
                item={item}
                onCancel={() =>
                  setCancelledItemIds((current) => {
                    const next = new Set(current);
                    next.add(item.id);
                    return next;
                  })
                }
              />
            ))}
          </View>

          {isReview && !showOutcome ? (
            <ReviewContent
              state={displayedState}
              selectedGroup={selectedReviewGroup}
              isReviewing={isReviewingGroup || isReviewingSingle}
              isReviewingSingle={isReviewingSingle}
              onSelectGroup={(group) => {
                setSelectedReviewGroup(group);
                setIsReviewingGroup(false);
              }}
              onBack={() => {
                setSelectedReviewGroup(null);
                setIsReviewingGroup(false);
              }}
            />
          ) : null}

          {isSourceOnly && !showOutcome ? (
            <ReviewContent
              state="source-only"
              selectedGroup={null}
              isReviewing={false}
              isReviewingSingle={false}
              onSelectGroup={() => undefined}
              onBack={() => undefined}
            />
          ) : null}

          {isPartial && !showOutcome ? (
            <View
              style={{ flexDirection: "row", gap: 9, alignItems: "flex-start" }}
            >
              <RefreshCw size={15} color={COLORS.error} strokeWidth={2} />
              <Text
                className="font-dm-sans"
                style={{
                  flex: 1,
                  color: COLORS.secondary,
                  fontSize: 11,
                  lineHeight: 16,
                }}
              >
                Retry only the file that failed. The saved files will not be
                uploaded again.
              </Text>
            </View>
          ) : null}

          <View style={{ gap: 9, marginTop: 1 }}>
            {showOutcome ? (
              <>
                <PrimaryButton label="Done" onPress={onClose} fullWidth />
                {isPartial ? (
                  <SecondaryButton
                    label={retryingFailed ? "Retrying…" : "Retry failed file"}
                    disabled={retryingFailed || retriedFailed}
                    onPress={() => setRetryingFailed(true)}
                    leading={
                      <RefreshCw
                        size={16}
                        color={semanticColors.text.secondary}
                        strokeWidth={2.2}
                      />
                    }
                  />
                ) : null}
              </>
            ) : null}
            {!showOutcome && isSourceOnly ? (
              <PrimaryButton label="Done" onPress={onClose} fullWidth />
            ) : null}
            {!showOutcome &&
            (displayedState === "single-upload" ||
              displayedState === "single-processing") &&
            !isQueue ? (
              <SecondaryButton
                label="Add more files"
                onPress={addMoreFiles}
                leading={
                  <Plus size={16} color={semanticColors.text.secondary} />
                }
              />
            ) : null}
            {!showOutcome && isQueue && isActive ? (
              <>
                <PrimaryButton
                  label="Continue in background"
                  onPress={onClose}
                  fullWidth
                />
                <SecondaryButton
                  label="Add more files"
                  disabled={additionalItems.length >= 3}
                  onPress={addMoreFiles}
                  leading={
                    <Plus size={16} color={semanticColors.text.secondary} />
                  }
                />
              </>
            ) : null}
            {!showOutcome && isBackground ? (
              <>
                <PrimaryButton
                  label="Continue in background"
                  onPress={onClose}
                  fullWidth
                />
                <SecondaryButton
                  label="Keep waiting"
                  onPress={() => undefined}
                />
              </>
            ) : null}
            {!showOutcome &&
            (displayedState === "single-review" ||
              displayedState === "ambiguous-review") ? (
              <>
                <PrimaryButton
                  label={
                    displayedState === "ambiguous-review" && !isReviewingSingle
                      ? "Review information"
                      : "Save information"
                  }
                  onPress={() => {
                    if (
                      displayedState === "ambiguous-review" &&
                      !isReviewingSingle
                    ) {
                      setIsReviewingSingle(true);
                      return;
                    }
                    setOutcome("saved");
                  }}
                  fullWidth
                />
                {displayedState === "single-review" || isReviewingSingle ? (
                  <SecondaryButton
                    label={isReviewingSingle ? "Back to information" : "Review"}
                    onPress={() => setIsReviewingSingle((current) => !current)}
                  />
                ) : null}
              </>
            ) : null}
            {!showOutcome && displayedState === "multi-review" ? (
              <>
                <PrimaryButton
                  label="Save"
                  onPress={() => setOutcome("saved")}
                  fullWidth
                />
                <SecondaryButton
                  label={isReviewingGroup ? "Back to updates" : "Review"}
                  onPress={() => {
                    if (isReviewingGroup) {
                      setIsReviewingGroup(false);
                      return;
                    }
                    if (!selectedReviewGroup) {
                      setSelectedReviewGroup("treatmentPlan");
                    }
                    setIsReviewingGroup(true);
                  }}
                />
              </>
            ) : null}
            {!showOutcome && isPartial ? (
              <PrimaryButton
                label="Continue with available files"
                onPress={() => setOutcome("continued")}
                fullWidth
              />
            ) : null}
            {!showOutcome && isPartial ? (
              <SecondaryButton
                label={retryingFailed ? "Retrying…" : "Retry failed file"}
                disabled={retryingFailed || retriedFailed}
                onPress={() => setRetryingFailed(true)}
                leading={
                  <RefreshCw
                    size={16}
                    color={semanticColors.text.secondary}
                    strokeWidth={2.2}
                  />
                }
              />
            ) : null}
          </View>
        </View>
      </ScrollView>
    </Dialog>
  );
}
